import _ from 'lodash'
import moment from 'moment-timezone'

import { Entity } from '../models/entity'
import { Formatter } from './formatter'

/**
 * Presentation helpers for Carrier Appointment Confirmation (CAC) entities,
 * shared by the native mobile and mobile-web pre-check screens so both render
 * titles, times and check-in status identically. Consumed as `CacUtils.method`.
 */

/** Check-in execution states that mean the workflow is over — not resumable. */
const TERMINAL_CHECKIN_STATES = ['Completed', 'Canceled']

const PATH_ORIGIN_LOCATION =
  "core_calendar_appointmentConfirmation.appointment.denormalizedProperties['core_yms_carrierAppointment.originLocation']"

const PATH_DESTINATION_LOCATION =
  "core_calendar_appointmentConfirmation.appointment.denormalizedProperties['core_yms_carrierAppointment.destinationLocation']"

const PATH_APPOINTMENT_DENORM =
  'core_calendar_appointmentConfirmation.appointment.denormalizedProperties'

const PATH_TIME_WINDOW =
  "core_calendar_appointmentConfirmation.appointment.denormalizedProperties['core_calendar_appointment.timeWindow']"

const PATH_FACILITY_DISPLAY_NAME =
  "core_calendar_appointmentConfirmation.appointment.denormalizedProperties['core_yms_carrierAppointment.facility'].displayName"

const PATH_CUSTOMER_DISPLAY_NAME =
  "core_calendar_appointmentConfirmation.appointment.denormalizedProperties['core_yms_carrierAppointment.facility'].denormalizedProperties['core_yms_facility.checkInPlanContainer'].denormalizedProperties['core_storyboard_plansContainer.activePlan'].denormalizedProperties['owner.firm'].displayName"

const PATH_APPOINTMENT_DISPLAY_NAME = 'core_calendar_appointmentConfirmation.appointment.displayName'

const PATH_CHECKIN_ENTITY_ID =
  "core_calendar_appointmentConfirmation.appointment.denormalizedProperties['core_yms_carrierAppointment.checkIn'].entityId"

const PATH_CHECKIN_STATUS =
  "core_calendar_appointmentConfirmation.appointment.denormalizedProperties['core_yms_carrierAppointment.checkIn'].denormalizedProperties['core_storyboard_execution.status']"

/**
 * Execution entityId for an in-progress check-in, used to load and resume the
 * storyboard execution from a CAC detail view.
 */
function extractCheckInEntityIdForResume(
  entity: Entity | undefined | null
): string | undefined {
  if (!entity) {
    return undefined
  }
  const appointmentDenorm = entity.get(PATH_APPOINTMENT_DENORM) as Record<string, unknown> | undefined
  const checkIn = appointmentDenorm?.['core_yms_carrierAppointment.checkIn'] as { entityId?: string } | undefined
  return checkIn?.entityId
}

/**
 * True when a check-in is linked and still in progress (i.e. not Completed or
 * Canceled), so it can be resumed. Treats a missing status as in-progress so a
 * not-yet-denormalized status never hides an active check-in.
 */
function hasCheckInStarted(entity: Entity): boolean {
  if (_.isNil(entity.get(PATH_CHECKIN_ENTITY_ID))) {
    return false
  }
  const status = entity.get(PATH_CHECKIN_STATUS) as string | undefined
  return status == null || !TERMINAL_CHECKIN_STATES.includes(status)
}

/**
 * Primary card line — "Customer · Facility", falling back to facility or displayName.
 */
function buildTitle(entity: Entity): string {
  const facilityName = entity.get(PATH_FACILITY_DISPLAY_NAME) as string | undefined
  const customerName = entity.get(PATH_CUSTOMER_DISPLAY_NAME) as string | undefined
  return _.compact([customerName, facilityName]).join(' · ') || entity.displayName || ''
}

function getAppointmentNumber(entity: Entity): string | undefined {
  return entity.get(PATH_APPOINTMENT_DISPLAY_NAME) as string | undefined
}

/**
 * Appointment start formatted as "Today/Tomorrow/MMM D · h:mm A" for the card meta line.
 */
function formatAppointmentTime(entity: Entity): string {
  const timeWindow = entity.get(PATH_TIME_WINDOW) as { start?: { dateTime?: string } } | undefined
  const dateTime = timeWindow?.start?.dateTime
  if (!dateTime) {
    return ''
  }
  const when = moment(dateTime)
  const now = moment()
  let day: string
  if (when.isSame(now, 'day')) {
    day = 'Today'
  } else if (when.isSame(now.clone().add(1, 'day'), 'day')) {
    day = 'Tomorrow'
  } else {
    day = when.format('MMM D')
  }
  return `${day} · ${when.format('h:mm A')}`
}

function getLocationName(entity: Entity, basePath: string): string | undefined {
  return entity.get(`${basePath}.displayName`) as string | undefined
}

function getLocationAddress(entity: Entity, basePath: string): string {
  const address = entity.get(`${basePath}.denormalizedProperties['location.address']`)
  return address ? Formatter.formatAddress(address) || '' : ''
}

function getPickupName(entity: Entity): string | undefined {
  return getLocationName(entity, PATH_ORIGIN_LOCATION)
}

function getPickupAddress(entity: Entity): string {
  return getLocationAddress(entity, PATH_ORIGIN_LOCATION)
}

function getDropoffName(entity: Entity): string | undefined {
  return getLocationName(entity, PATH_DESTINATION_LOCATION)
}

function getDropoffAddress(entity: Entity): string {
  return getLocationAddress(entity, PATH_DESTINATION_LOCATION)
}

function getShipmentId(entity: Entity): string | undefined {
  return entity.get('core_yms_carrierAppointmentConfirmation.shipmentId') as string | undefined
}

function getCarrierName(entity: Entity): string | undefined {
  return entity.get('core_yms_carrierAppointmentConfirmation.carrier.displayName') as string | undefined
}

function getTrailer(entity: Entity): string | undefined {
  return entity.get('core_yms_carrierAppointmentConfirmation.trailerNumber1') as string | undefined
}

export const CacUtils = {
  extractCheckInEntityIdForResume,
  hasCheckInStarted,
  buildTitle,
  getAppointmentNumber,
  formatAppointmentTime,
  getPickupName,
  getPickupAddress,
  getDropoffName,
  getDropoffAddress,
  getShipmentId,
  getCarrierName,
  getTrailer,
}
